<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { cubicOut } from "svelte/easing";
  import { tweened } from "svelte/motion";
  import { type TransitionConfig, type EasingFunction, fade, slide } from "svelte/transition";
  import { touchdrag, type TouchdragEvent } from "./touch";
  import Velocity from "touch-velocity";
  import { appendToBody } from "./utils";

  const dispatch = createEventDispatcher<{ close: void }>();

  export let open: boolean;

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") close();
  }

  function close() {
    if (open) {
      dispatch("close");
      open = false;
    }
  }

  const histroyHelper = function (config: { open?: () => void; close?: () => void }) {
    const uniqueId = `${performance.now}`;

    if (!history.state || history.state[uniqueId] !== "open") {
      history.replaceState(Object.assign({}, history.state, { [uniqueId]: "close" }), document.title);
      history.pushState({ [uniqueId]: "open" }, document.title + " open");
    }

    function handler(e) {
      if (e.state[uniqueId] === "close" && config.close) {
        config.close();
      } else if (e.state[uniqueId] === "open" && config.open) {
        config.open();
      }
    }

    window.addEventListener("popstate", handler);
    return () => {
      window.removeEventListener("popstate", handler);
    };
  };

  // iOS Hack to simolate overscrollBehavior css property:
  function safariOverscrollBehavior() {
    let scrollY: number;
    let scrollX: number;

    const body = document.body;
    const html = document.querySelector("html");

    function syncHeight() {
      document.documentElement.style.setProperty("--window-inner-height", `${window.innerHeight}px`);
    }

    function preventDefault(e) {
      e.preventDefault();
    }

    return {
      none(overlay: HTMLElement) {
        // remember scroll position
        scrollX = window.scrollX;
        scrollY = window.scrollY;

        syncHeight();
        html.style.height = body.style.height = `calc(var(--window-inner-height) - 1px)`;
        html.style.overflow = body.style.overflow = `hidden`;
        html.style.boxSizing = body.style.boxSizing = `border-box`;

        body.style.position = `fixed`;
        body.style.top = `-${scrollY}px`;
        body.style.left = `-${scrollX}px`;
        body.style.width = `100vw`;

        overlay.addEventListener("pointermove", preventDefault);
        // overlay.addEventListener("touchmove", preventDefault);
        window.addEventListener("resize", syncHeight);
      },

      auto(overlay: HTMLElement) {
        html.style.height = body.style.height = ``;
        html.style.overflow = body.style.overflow = ``;
        html.style.boxSizing = body.style.boxSizing = ``;

        body.style.position = ``;
        body.style.top = ``;
        body.style.left = ``;
        body.style.width = ``;

        overlay.removeEventListener("pointermove", preventDefault);
        // overlay.removeEventListener("touchmove", preventDefault);
        window.removeEventListener("resize", syncHeight);

        // restore scroll position
        window.scrollTo(scrollX, scrollY);
      },
    };
  }

  function OverscrollLock() {
    // prevent "pull to refresh"

    const supportsOverscroll = isOverscrollBehaviorSupported();
    console.info("overscrollBehavior supported: ", supportsOverscroll);
    const safari = safariOverscrollBehavior();

    return {
      lock(node: HTMLElement) {
        document.body.style.overscrollBehavior = "none";
        document.body.style.overflow = "hidden";
        if (!supportsOverscroll) {
          safari.none(node);
        }
      },
      release(node: HTMLElement, duration: number) {
        setTimeout(() => {
          document.body.style.overscrollBehavior = "";
          document.body.style.overflow = "";
          if (!supportsOverscroll) {
            safari.auto(node);
          }
        }, duration);
      },
    };

    function isOverscrollBehaviorSupported() {
      const div = document.body.appendChild(document.createElement("div"));
      try {
        div.style.overscrollBehavior = "none";
        const style = getComputedStyle(div);
        return style.overscrollBehavior === "none";
      } finally {
        div.remove();
      }
    }
  }

  const overscrollLock = OverscrollLock();

  function AnimationController() {
    let _state: number = 0;
    let _dragState: number = 0;
    let _node: HTMLDivElement;
    let _slideAnimation: Animation;
    let velocity: Velocity;

    function fixPadding() {
      const content = _node.querySelector<HTMLDivElement>(".sheet-content");
      const footer = _node.parentElement.querySelector<HTMLDivElement>(".sheet-footer");
      content.style.paddingBottom = `${footer.offsetHeight + 20}px`;
    }

    return {
      init(node: HTMLDivElement) {
        _node = node;
        _slideAnimation = SlideAnimation(_node);
      },
      getInitialOffset() {
        fixPadding();
        const parentHeight = _node.parentElement.clientHeight;
        const bodyHeight = _node.querySelector<HTMLDivElement>(".sheet-content").offsetHeight;
        const maxHeight = parentHeight * 0.8;
        const height = Math.min(bodyHeight, maxHeight);
        const offset = height / parentHeight;
        return offset;
      },
      state() {
        return _state;
      },

      openTick() {
        const state = this.getInitialOffset();
        return (t: number) => this.tick(t * state);
      },
      closeTick() {
        const state = _state;
        return (t: number) => this.tick(t * state);
      },

      touchstart(e: TouchEvent) {
        _dragState = _state;
        velocity = new Velocity();
      },
      touchend(e: TouchEvent) {
        const speed = velocity.getVelocity();
        const speedThreshold = 400;

        // autoanimate..
        if (_state > 0.9 || speed < -speedThreshold) {
          // slide open:
          this.animate(1, duration);
        }
        // slide close:
        else if (speed > speedThreshold) {
          const offset = this.getInitialOffset();
          if (_state > offset) {
            this.animate(offset, duration);
          } else if (_state < offset) {
            close();
          }
        }

        // close if only footer is visible
        const footerHeight = _node.parentElement.querySelector<HTMLDivElement>(".sheet-footer").offsetHeight;
        const minOffset = (footerHeight + 20) / _node.parentElement.offsetHeight;
        if (_state <= minOffset) {
          close();
        }

        _dragState = 0;
      },
      touchdrag(e: TouchdragEvent) {
        const { dir, yStart, yDiff, yNow } = e.detail;
        const parentHeight = _node.parentElement.clientHeight;

        velocity.updatePosition(yNow);

        const currentPx = parentHeight * _dragState;
        const nextPx = currentPx + yDiff;
        const t = nextPx / parentHeight;

        if (_state < 1 || _node.scrollTop === 0) {
          this.tick(t);
        }
      },

      animate(t: number, duration: number) {
        const transition = tweened(_state, { duration, easing: cubicOut });
        const unsub = transition.subscribe((t) => this.tick(t));
        transition.set(t).then(() => unsub());
      },
      tick(t: number) {
        t = Math.min(1, Math.max(0, t));
        _state = t;
        _slideAnimation.tick(t);
      },
    };

    type Animation = { tick(t: number): void };

    function SlideAnimation(node: HTMLDivElement): Animation {
      const borderAnimation = BorderAnimation(node);

      return {
        tick: (t: number) => {
          const parentHeight = node.parentElement.clientHeight;

          const translate = parentHeight - t * parentHeight;

          if (translate === 0) {
            node.style.overflow = "";
          } else {
            node.style.overflow = "hidden";
          }

          node.style.transform = `translateY(${translate}px)`;

          if (t > 0.8) {
            const scale = (t - 0.8) / 0.2;
            borderAnimation.tick(scale);
          }
        },
      };
    }

    function BorderAnimation(node: HTMLDivElement, topLeftRadius = 0, topRightRadius = 0): Animation {
      const style = getComputedStyle(node);
      const borderTopLeftRadius = parseFloat(style.borderTopLeftRadius);
      const borderTopRightRadius = parseFloat(style.borderTopRightRadius);

      return {
        tick: (t: number) => {
          node.style.borderTopLeftRadius = `${borderTopLeftRadius - t * (borderTopLeftRadius - topLeftRadius)}px`;
          node.style.borderTopRightRadius = `${borderTopRightRadius - t * (borderTopRightRadius - topRightRadius)}px`;
        },
      };
    }
  }

  const animationController = AnimationController();

  type Params = {
    delay?: number;
    duration?: number;
    easing?: EasingFunction;
  };

  let histroyUnsubscribe: Function;

  function slideOpen(node: HTMLDivElement, { delay, duration, easing = cubicOut }: Params = {}): TransitionConfig {
    animationController.init(node);

    histroyUnsubscribe = histroyHelper({ close });
    overscrollLock.lock(node.parentElement);

    return {
      delay,
      duration,
      easing,
      tick: animationController.openTick(),
    };
  }

  function slideClose(node: HTMLDivElement, { delay, duration, easing = cubicOut }: Params = {}): TransitionConfig {
    // animationController.init(node);
    histroyUnsubscribe();
    overscrollLock.release(node.parentElement, duration * 0.99);
    return {
      delay,
      duration,
      easing,
      tick: animationController.closeTick(),
    };
  }

  function update() {
    const offset = animationController.getInitialOffset();
    if (offset > animationController.state()) {
      animationController.animate(offset, 300);
    }
  }

  const duration = 500;
</script>

<svelte:window on:keydown={handleKeydown} />

<div use:appendToBody>
  {#if open}
    <div class="sheet overlay-zindex" in:fade={{ duration: duration * 0.2 }} out:fade={{ duration }} on:click={(_) => close()} use:touchdrag on:touchstart={(e) => animationController.touchstart(e)} on:touchend={(e) => animationController.touchend(e)} on:touch-drag={(e) => animationController.touchdrag(e)}>
      <div class="sheet-body" in:slideOpen={{ duration }} out:slideClose={{ duration }} on:click|stopPropagation>
        <div class="sheet-content">
          <slot overlay={{ close, update }} />
        </div>
      </div>
      <div class="sheet-footer" transition:slide={{ duration }} on:click|stopPropagation><slot name="footer" /></div>
    </div>
  {/if}
</div>

<style lang="scss">
  .overlay-zindex {
    z-index: 10000000;
  }

  .sheet {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(#000, 0.8);
    display: flex;
    flex-flow: column;
    justify-content: flex-end;
    // disable "pull to refresh"
    overscroll-behavior: none;
  }

  .sheet-body {
    position: relative;
    background-color: #fff;
    box-shadow: 0px 2px 35px -2px rgba(0, 0, 0, 0.15);
    border-radius: 22px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    height: 100%;
    overflow: auto;
    -webkit-overflow-scrolling: touch;

    &:before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      right: 0;
      display: block;
      width: 1.2rem;
      height: 3px;
      background-color: #d9d9d9;
      border-radius: 99px;
      margin: 0.8rem auto 0;
    }
  }

  .sheet-footer {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #fff;
    padding: 20px;
    box-shadow: 0 0 0 1px #d9d9d9;

    &:empty {
      display: none;
    }
  }

  .sheet-content {
    padding: 20px;
  }
</style>
